import React, { useEffect, useState } from "react";
import { formatTimeToAMPM } from "../utils/helperFunctions";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

const PDFContent = ({
  formData,
  filePreviews,
  selectedRoom,
  guestPreviewUrls,
  showPreview,
  setShowPdfPreview,
  isdownloadPdf,
  setPdfdata,
  setisdownloadPdf,
  onPdfDownloaded
}) => {
  
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [totalImages, setTotalImages] = useState(0);
  

  // Calculate total number of images
  useEffect(() => {
    let count = 0;
    
    // Count main guest documents
    if (formData?.documentUrl) {
      count += formData.documentUrl.length;
    }
    
    // Count other guests documents
    if (formData?.otherGuests) {
      formData.otherGuests.forEach(guest => {
        if (guest?.documentUrl) {
          count += guest.documentUrl.length;
        }
      });
    }
    
    setTotalImages(count);
  }, [formData]);

  useEffect(() => {
    if (showPreview) {
      generatePDF();
    } else {
      setPdfPreviewUrl(null);
    }
    if(isdownloadPdf){
      generatePDF();
    }
  }, [showPreview,isdownloadPdf]);

  const generatePDF = async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    const element = document.getElementById("pdf-content");
    element.style.display = "block";

    setTimeout(async () => {
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 10;
        const bottomPadding = 20;

        const canvas = await html2canvas(element, { scale: 2, useCORS: true });
        const imgWidth = pageWidth - 2 * margin;

        let yPosition = 0;

        while (yPosition < canvas.height) {
          if (yPosition > 0) pdf.addPage();

          const pageCanvas = document.createElement("canvas");
          const context = pageCanvas.getContext("2d");

          pageCanvas.width = canvas.width;
          pageCanvas.height = Math.min(
            canvas.height - yPosition,
            (pageHeight - bottomPadding) * (canvas.width / imgWidth)
          );

          context.drawImage(
            canvas,
            0,
            yPosition,
            canvas.width,
            pageCanvas.height,
            0,
            0,
            pageCanvas.width,
            pageCanvas.height
          );

          const imgData = pageCanvas.toDataURL("image/jpeg", 1.0);
          pdf.addImage(
            imgData,
            "JPEG",
            margin,
            margin,
            imgWidth,
            (pageCanvas.height * imgWidth) / canvas.width
          );

          yPosition += pageCanvas.height;
        }

        const blob = pdf.output("blob");
        const url = URL.createObjectURL(blob);
        if(isdownloadPdf){
          pdf.save(getFormattedFilename(formData));
          setPdfdata(null)
          setisdownloadPdf(false)
          toast.success("PDF saved successfully!");
          onPdfDownloaded()
        }else{
          setPdfPreviewUrl(url);
        }
       
      } catch (error) {
        console.log(error)
        console.error("Error generating PDF preview:", error);
      } finally {
        element.style.display = "none";
       
        setIsGenerating(false);
      }
    }, 200);
  };

  const getFormattedFilename = (formData) => {
    const parts = [];
    if (selectedRoom?.roomNo) {
      parts.push(selectedRoom.roomNo);
    }
    if(formData?.roomNo){
      parts.push(formData.roomNo);
    }
    if (formData.sameDayCheckInCount) {
      parts.push(formData.sameDayCheckInCount);
    }
    const guestFullName = `${formData.firstName || ""}${
      formData.middleName ? `_${formData.middleName}` : ""
    }${formData.lastName ? `_${formData.lastName}` : ""}`
      .trim()
      .replace(/\s+/g, "_");
    if (guestFullName) {
      parts.push(guestFullName);
    }
    if (formData.mobileNumber) {
      parts.push(formData.mobileNumber);
    }
    if (formData.otherGuests?.[0]?.fullName) {
      parts.push(formData.otherGuests[0].fullName.replace(/\s+/g, "_"));
    }
    const checkInDate = formData.checkInDate
      ? new Date(formData.checkInDate)
      : new Date();
    const formattedDate = checkInDate
      .toLocaleDateString("en-GB")
      .split("/")
      .join("");
    const formattedTime = formData.checkInTime
      ? formData.checkInTime.replace(":", "")
      : checkInDate.getHours().toString().padStart(2, "0") +
        checkInDate.getMinutes().toString().padStart(2, "0");
    parts.push(formattedDate);
    parts.push(formattedTime);
    return `${parts.join("_")}.pdf`;
  };

  const savePdfToFolder = async () => {
    if (!pdfPreviewUrl) {
      toast.error("No PDF available to save");
      return;
    }
    const fileName = getFormattedFilename(formData);

    try {
      // Fetch the PDF
      const response = await fetch(pdfPreviewUrl);
      if (!response.ok) throw new Error("Failed to fetch PDF");
      const pdfBlob = await response.blob();

      // Modern browsers (Chrome, Edge)
      if ("showSaveFilePicker" in window) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [
              {
                description: "PDF Files",
                accept: { "application/pdf": [".pdf"] },
              },
            ],
          });

          const writable = await handle.createWritable();
          await writable.write(pdfBlob);
          await writable.close();
          toast.success("PDF saved successfully!");
          return;
        } catch (err) {
          if (err.name !== "AbortError") {
            console.error("Save dialog error:", err);
            throw err; // Fall through to legacy method
          }
          return; // User cancelled
        }
      }

      // Legacy method for other browsers
      const blobUrl = URL.createObjectURL(pdfBlob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      toast.success("PDF download started!");
    } catch (err) {
      console.error("Save failed:", err);
      toast.error(`Failed to save PDF: ${err.message || "Unknown error"}`);
    }
  };
  const getImageGridConfig = () => {
    if (totalImages === 0) return { className: "" };
    
    switch (totalImages) {
      case 1:
      case 2:
      case 3:
      case 4:
        return { className: "grid-cols-1" };
      case 5:
      case 6:
        return { className: "grid-cols-2" };
      default: // 7 or more
        return {  className: "grid-cols-2" };
    }
  };

  const getImageSizeClass = () => {
    return "h-auto"; 
  };

  const { className: gridClassName } = getImageGridConfig();
  const imageSizeClass = getImageSizeClass();

  // if (!showPreview) return null;

  return (
    <>
      <div
        id="pdf-content"
        className="hidden absolute left-[-9999px] w-[250mm] bg-white"
      >
        <div className="w-full h-full flex">
          <div className="w-1/2 pr-4 text-center border-r border-gray-300 flex flex-col items-center">
            <h3 className="text-lg font-semibold mb-3">Uploaded Documents</h3>
            <div className={`grid ${gridClassName} gap-4 w-full`}>
              {formData?.documentUrl?.map((file, index) => {
                const previewUrl = filePreviews?.[index];
                const imageUrl = previewUrl
                  ? previewUrl
                  : typeof file === "string"
                  ? `${process.env.REACT_APP_BASE_URL}/${file}`             
                  : URL.createObjectURL(file);
            

                return (
                  <img
                    key={index}
                    src={imageUrl}
                    alt="Uploaded"
                    className={`w-full ${imageSizeClass} object-cover rounded-md shadow border`}
                    // className="w-full h-30 object-cover rounded-md shadow border"
                  />
                );
              })}
              {formData?.otherGuests?.map((guest, gIndex) =>
                guest?.documentUrl?.map((file, fIndex) => {
                  const imageUrl =
                    typeof file === "string"
                      ? `${process.env.REACT_APP_BASE_URL}/${file}`
                      : guestPreviewUrls[gIndex]?.[fIndex] || "";

                  return (
                    <img
                      key={`guest-${gIndex}-${fIndex}`}
                      src={imageUrl}
                      alt={`Other Guest ${gIndex + 1} Document`}
                      className={`w-full ${imageSizeClass} object-cover rounded-md shadow border`}
                      // className="w-full h-30 object-cover rounded-md shadow border"
                    />
                  );
                })
              )}
            </div>
          </div>

          <div className="w-1/2 pl-4">
          {formData?.roomNo && (
              <p className="text-lg font-medium">
                <strong>Room Number:</strong> {formData.roomNo}
              </p>
            )}
            {selectedRoom?.roomNo && (
              <p className="text-lg font-medium">
                <strong>Room Number:</strong> {selectedRoom.roomNo}
              </p>
            )}
            {(formData.firstName ||
              formData.middleName ||
              formData.lastName) && (
              <p className="text-lg font-medium">
                <strong>Full Name:</strong> {formData?.firstName}{" "}
                {formData.middleName} {formData.lastName}
              </p>
            )}

            {formData.companyName && (
              <p>
                <strong>Company Name:</strong> {formData?.companyName}
              </p>
            )}

            {formData.mobileNumber && (
              <p>
                <strong>Mobile Number:</strong> {formData.mobileNumber}
              </p>
            )}

            {formData.phoneNumber && (
              <p>
                <strong>Phone Number:</strong> {formData.phoneNumber}
              </p>
            )}

            {formData.emailAddress && (
              <p>
                <strong>Email Address:</strong> {formData.emailAddress.toUpperCase()}
              </p>
            )}

            {formData.dateOfBirth && (
              <p>
                <strong>Date of Birth:</strong>
                {formData.dateOfBirth
                  ? new Date(formData.dateOfBirth)
                      .toLocaleDateString("en-GB")
                      .replace(/\//g, "-")
                  : ""}
              </p>
            )}

            {(formData.address?.houseFlatNo ||
              formData.address?.address ||
              formData.address?.locality ||
              formData.address?.city ||
              formData.address?.district ||
              formData.address?.state ||
              formData.address?.zipCode) && (
              <>
                <h3 className="text-lg font-semibold mt-4">Address</h3>
                <p>
                  {formData.address.houseFlatNo &&
                    `${formData.address.houseFlatNo}, `}
                  {formData.address.address && `${formData.address.address}, `}
                  {formData.address.locality &&
                    `${formData.address.locality}, `}
                  {formData.address.city && `${formData.address.city}, `}
                  {formData.address.district &&
                    `${formData.address.district}, `}
                  {formData.address.state &&
                    `${formData.address.state.toUpperCase()}, `}
                  {formData.address.country &&
                    `${formData.address.country.toUpperCase()}`}
                  {formData.address.zipCode && ` - ${formData.address.zipCode}`}
                </p>
              </>
            )}
            {(formData.checkInDate ||
              formData.checkInTime ||
              formData.checkOutDate ||
              formData.checkOutTime ||
              formData.numberOfAdults ||
              formData.numberOfChildren) && (
              <>
                <h3 className="text-lg font-semibold mt-4">
                  Travel & Stay Information
                </h3>
                {formData.checkInDate && (
                  <p>
                    <strong>Check-In Date/Time:</strong>
                    {new Date(formData.checkInDate)
                      .toLocaleDateString("en-GB")
                      .replace(/\//g, "-")}
                    /{formatTimeToAMPM(formData.checkInTime)}
                  </p>
                )}
                {formData.checkOutDate && (
                  <p>
                    <strong>Check-Out Date/Time:</strong>
                    {new Date(formData.checkOutDate)
                      .toLocaleDateString("en-GB")
                      .replace(/\//g, "-")}
                    /{formatTimeToAMPM(formData.checkOutTime)}
                  </p>
                )}
                {formData.numberOfAdults !== undefined && (
                  <p>
                    <strong>Number of Adults:</strong> {formData.numberOfAdults}
                  </p>
                )}
                {formData.numberOfChildren !== undefined && (
                  <p>
                    <strong>Number of Children:</strong>
                    {formData.numberOfChildren}
                  </p>
                )}
              </>
            )}

            {(formData.documentType ||
              formData.documentNumber ||
              formData.signature) && (
              <>
                <h3 className="text-lg font-semibold mt-4">Documents</h3>
                {formData.documentType && (
                  <p>
                    <strong>Document Type:</strong> {formData.documentType}
                  </p>
                )}
                {formData.documentNumber && (
                  <p>
                    <strong>Document Number:</strong> {formData.documentNumber}
                  </p>
                )}

                <div className="flex mt-4 justify-end gap-2">
                  <div>
                    <strong>Signature:</strong>
                    <div className="w-40 h-16 border-2 border-black flex items-center justify-center mt-2 bg-gray-50">
                      {formData.signature ? (
                        // isEdit &&
                        typeof formData.signature === "string" &&
                        !formData.signature.startsWith("data:image") ? (
                          <img
                            src={`${process.env.REACT_APP_BASE_URL}/${formData.signature}`}
                            alt="Signature Preview"
                            className="mt-2 border rounded-lg inline w-[100%] h-[100%]"
                          />
                        ) : (
                          <img
                            src={formData.signature}
                            alt="Signature Preview"
                            className="mt-2 border rounded-lg inline w-[100%] h-[100%]"
                          />
                        )
                      ) : (
                        <span>No signature</span>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {formData.otherGuests?.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mt-4">Other Guests</h3>
                {formData.otherGuests.map((guest, index) => (
                  <div
                    key={index}
                    className="border-t border-gray-300 mt-3 pt-2"
                  >
                    <div className="grid grid-cols-2 gap-x-4">
                      {guest.fullName && (
                        <div>
                          <strong>Full Name:</strong> {guest.fullName}
                        </div>
                      )}
                      {guest.mobileNumber && (
                        <div>
                          <strong>Mobile Number:</strong> {guest.mobileNumber}
                        </div>
                      )}
                      {guest.documentType && (
                        <div>
                          <strong>Document Type:</strong> {guest.documentType}
                        </div>
                      )}
                      {guest.documentNumber && (
                        <div>
                          <strong>Document Number:</strong>
                          {guest.documentNumber}
                        </div>
                      )}
                      {guest.dateOfBirth && (
                        <div>
                          <strong>Date of Birth:</strong>
                          {new Date(guest.dateOfBirth)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")}
                        </div>
                      )}
                      {guest.address && (
                        <div>
                          <strong>Address:</strong>
                          {guest.address}
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end ">
                      <div>
                        <strong>Signature:</strong>
                        <div className="w-40 h-16 border-2 border-black flex items-center justify-center bg-gray-50 mt-2">
                          {guest.signature ? (
                            // isEdit &&
                            typeof guest.signature === "string" &&
                            !guest.signature.startsWith("data:image") ? (
                              <img
                                src={`${process.env.REACT_APP_BASE_URL}/${guest.signature}`}
                                alt={`Guest ${index + 1} Signature`}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <img
                                src={guest.signature}
                                alt={`Guest ${index + 1} Signature`}
                                className="w-full h-full object-contain"
                              />
                            )
                          ) : (
                            <span>No signature</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {pdfPreviewUrl && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => {
              setPdfPreviewUrl(null);
              setShowPdfPreview(false);
            }}
          ></div>
          <div className="bg-white p-4 rounded-md shadow-lg w-[60%] relative h-[90vh] flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">PDF Preview</h3>
              <button
                onClick={() => {
                  setPdfPreviewUrl(null);
                  setShowPdfPreview(false);
                }}
                className="text-gray-500 hover:text-red-600 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            {/* #toolbar=0 */}
            <div className="flex-1">
              <iframe
                src={`${pdfPreviewUrl}`}
                width="100%"
                height="100%"
                title="PDF Preview"
                className="border border-gray-300 rounded-md"
              ></iframe>
            </div>

            <div className="flex justify-end mt-3">
              <button
                onClick={savePdfToFolder}
                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-xs sm:text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PDFContent;
